import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Button, PocketPanel } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { usePopoverDismiss } from "@/hooks/use-popover-dismiss";
import { useT } from "@/i18n";
import { desktopScreenRecordingMessages } from "@/i18n/messages/desktop-screen-recording";
import { useScreenRecordingMedia } from "./screen-recording-media";
import { ScreenRecordingVideo } from "./screen-recording-video";
import { useScreenRecordingPreviewNativeDismiss } from "./use-screen-recording-preview-native-dismiss";

export function ScreenRecordingPreview({
  clipId,
  number,
  path,
  poster,
  anchorRef,
  onClose,
}: {
  clipId: string;
  number: number;
  path: string;
  poster?: string;
  anchorRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const t = useT(desktopScreenRecordingMessages);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 12, top: 12 });
  const media = useScreenRecordingMedia((state) => state.clips[clipId]);
  useScreenRecordingPreviewNativeDismiss({ anchorRef, contentRef, onClose });
  usePopoverDismiss({
    open: true,
    onClose,
    anchorRef,
    contentRef,
    stopEscapePropagation: true,
  });
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const content = contentRef.current;
    if (!anchor || !content) return;
    const place = () => {
      const bounds = anchor.getBoundingClientRect();
      const size = content.getBoundingClientRect();
      const left = Math.max(
        12,
        Math.min(
          bounds.left + bounds.width / 2 - size.width / 2,
          window.innerWidth - size.width - 12,
        ),
      );
      const below = bounds.bottom + 8;
      const top =
        below + size.height <= window.innerHeight - 12
          ? below
          : Math.max(12, bounds.top - size.height - 8);
      setPosition({ left, top });
    };
    place();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    observer?.observe(content);
    observer?.observe(
      anchor.closest('[data-testid="screen-recording-status"]') ??
        anchor.parentElement ??
        anchor,
    );
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", close, true);
    return () => window.removeEventListener("keydown", close, true);
  }, [onClose]);
  return createPortal(
    <PocketPanel
      ref={contentRef}
      nativeGroup="screen-recording-preview"
      testId={`screen-recording-preview-${clipId}`}
      role="dialog"
      aria-label={t("previewClip", { number })}
      className="pointer-events-auto fixed z-[10040] w-[min(360px,calc(100vw-24px))] gap-2"
      style={position}
      data-interactive="true"
      data-no-drag=""
      data-input-bar-root=""
      data-task-pocket-owned=""
      data-tray-popover="true"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-2 text-appkit-caption">
        <span>{t("clip", { number })}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          aria-label={t("closePreview")}
          data-testid={`screen-recording-preview-close-${clipId}`}
          onClick={onClose}
        >
          <Icons.X className="h-3 w-3" aria-hidden="true" />
        </Button>
      </div>
      <ScreenRecordingVideo clipId={clipId} poster={poster} preview />
      {!media?.url && media?.error ? (
        <p className="break-all text-appkit-caption text-secondary">
          {t("savedAt", { path })}
        </p>
      ) : null}
    </PocketPanel>,
    document.body,
  );
}
